import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import * as XLSX from 'xlsx'
import { Box, Text, VStack, useToast } from '@chakra-ui/react'
import headerMapping from '../../../../constants/excelHeaderMapping'
import { LABEL, MESSAGE } from '../../../../constants'
import { getArticles } from '../../api/getNomenclatures'

const ExcelDragAndDrop = ({
  onDataParsed,
  projects,
  costCenters,
  expenseGroup,
  cif
}) => {
  const toast = useToast()
  const [errorList, setErrorList] = useState([])
  const cleanString = (str) => (str === 0 || str ? String(str).trim() : '')

  const normalize = (s) =>
    String(s ?? '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()

  const toNumber = (val, decimals = 2) => {
    if (val === null || val === undefined || val === '') return null

    if (typeof val === 'number') {
      return Number(val.toFixed(decimals))
    }
  }

  const onDrop = useCallback(
    (acceptedFiles) => {
      setErrorList([])
      const file = acceptedFiles[0]
      const reader = new FileReader()

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

        if (!jsonData.length) {
          setErrorList([MESSAGE.EXCEL.error_data])
          return
        }

        const headers = jsonData[0].map(cleanString)
        const rows = jsonData.slice(1)
        const documentLines = rows.map((row) => {
          const line = {}
          headers.forEach((header, index) => {
            const mappedKey = headerMapping[header] || header
            if (
              ['unitPriceWithoutVat', 'quantity', 'vatValue'].includes(
                mappedKey
              )
            ) {
              line[mappedKey] = toNumber(row[index])
            } else {
              line[mappedKey] = cleanString(row[index])
            }
          })
          return line
        })

        const rowErrorMap = {}
        const addRowError = (key, message, rowNumber) => {
          if (!rowErrorMap[key]) {
            rowErrorMap[key] = { message, rows: [] }
          }
          rowErrorMap[key].rows.push(rowNumber)
        }

        const processedLines = documentLines.map((row, rowIndex) => {
          const currentRow = rowIndex + 2
          if (row.objectNumber) {
            const projectItem = projects.find(
              (p) => p.code.toString() === row.objectNumber
            )
            if (!projectItem) {
              addRowError(
                `invalid_project:${row.objectNumber}`,
                `ID Proiect "${row.objectNumber}" nu este valid.`,
                currentRow
              )
            }
          }

          if (row.vatValue == null || row.vatValue === '') {
            addRowError('missing_vatValue', 'Lipseste valoarea TVA', currentRow)
          } else {
            const numericVat = parseFloat(row.vatValue)
            const allowedVats = [21, 19, 11, 9, 5, 0]
            if (isNaN(numericVat) || !allowedVats.includes(numericVat)) {
              addRowError(
                `invalid_vatValue:${row.vatValue}`,
                `Valoare TVA "${
                  row.vatValue
                }" nu este valida. Valorile permise sunt: ${allowedVats.join(
                  ', '
                )}"`,
                currentRow
              )
            }
          }

          if (!row.costCenter) {
            addRowError(
              'missing_cost_center',
              'Lipseste centrul de cost.',
              currentRow
            )
          } else {
            const costCenterItem = costCenters.find(
              (center) => center.code.toString() === row.costCenter
            )
            if (!costCenterItem) {
              addRowError(
                `invalid_cost_center:${row.costCenter}`,
                `Centru de cost "${row.costCenter}" nu este valid.`,
                currentRow
              )
            }
          }

          if (!row.expenseGroup) {
            addRowError(
              'missing_expense_group',
              'Lipseste grupa de cheltuiala.',
              currentRow
            )
          } else if (!expenseGroup.includes(row.expenseGroup)) {
            addRowError(
              `invalid_expense_group:${row.expenseGroup}`,
              `Grupa de cheltuiala "${row.expenseGroup}" nu este valida.`,
              currentRow
            )
          }

          return row
        })

        const processedLinesTimestamp = processedLines.map((row) => {
          if (row.invoicePeriodStartDate) {
            const numericStart = parseFloat(row.invoicePeriodStartDate)
            if (!isNaN(numericStart)) {
              row.invoicePeriodStartDate = (numericStart - 25569) * 86400000
            } else {
              row.invoicePeriodStartDate = new Date(
                row.invoicePeriodStartDate
              ).getTime()
            }
          }
          if (row.invoicePeriodEndDate) {
            const numericEnd = parseFloat(row.invoicePeriodEndDate)
            if (!isNaN(numericEnd)) {
              row.invoicePeriodEndDate = (numericEnd - 25569) * 86400000
            } else {
              row.invoicePeriodEndDate = new Date(
                row.invoicePeriodEndDate
              ).getTime()
            }
          }
          return row
        })

        const rowErrors = Object.values(rowErrorMap).map((entry) => {
          const rowLabel =
            entry.rows.length > 1
              ? `Randurile ${entry.rows.join(', ')}`
              : `Rand ${entry.rows[0]}`
          return `${rowLabel}: ${entry.message}`
        })

        const articlesMap = {}
        processedLinesTimestamp.forEach((row, index) => {
          if (row.article) {
            if (!articlesMap[row.article]) {
              articlesMap[row.article] = []
            }
            articlesMap[row.article].push(index + 2)
          }
        })
        const uniqueArticles = Object.keys(articlesMap)

        const handleErrors = (additionalErrors = []) => {
          const combinedErrors = [...rowErrors, ...additionalErrors]
          if (combinedErrors.length > 0) {
            setErrorList(combinedErrors)
          } else {
            onDataParsed(processedLinesTimestamp)
            toast({
              title: 'Success',
              description: MESSAGE.EXCEL.success,
              status: 'success',
              duration: 3000,
              isClosable: true
            })
          }
        }

        if (uniqueArticles.length > 0) {
          Promise.all(
            uniqueArticles.map((article) =>
              getArticles({ searchTerm: article, cif })
                .then((res) => (Array.isArray(res?.content) ? res.content : []))
                .catch(() => [])
            )
          )
            .then((resultsArrays) => {
              const existingArticles = resultsArrays.flat()

              const existingSet = new Set(
                existingArticles
                  .map((e) => e?.name)
                  .filter(Boolean)
                  .map(normalize)
              )

              const articleErrors = uniqueArticles.reduce((acc, article) => {
                if (!existingSet.has(normalize(article))) {
                  const rowsStr = articlesMap[article].join(', ')
                  acc.push(
                    `Articolul "${article}" nu a fost gasit (Randurile: ${rowsStr}).`
                  )
                }
                return acc
              }, [])

              handleErrors(articleErrors)
            })
            .catch(() => {
              handleErrors(['Nu au putut fi validate articolele'])
            })
        } else {
          handleErrors()
        }
      }

      reader.onerror = () => {
        toast({
          title: 'Error',
          description: MESSAGE.EXCEL.error_read,
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }

      reader.readAsArrayBuffer(file)
    },
    [onDataParsed, toast, projects, costCenters, expenseGroup]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ['.xlsx', '.xls'],
    maxFiles: 1
  })

  return (
    <>
      <VStack
        spacing={4}
        p={5}
        border="2px dashed #CBD5E0"
        borderRadius="md"
        align="center"
      >
        <Box {...getRootProps()} p={5} textAlign="center" cursor="pointer">
          <input {...getInputProps()} />
          {isDragActive ? (
            <Text>{LABEL.EXCEL.isDragging}</Text>
          ) : (
            <Text>{LABEL.EXCEL.isNotDragging}</Text>
          )}
        </Box>
      </VStack>

      {errorList.length > 0 && (
        <Box
          mt={4}
          maxH="200px"
          overflowY="auto"
          border="1px solid"
          borderColor="red.500"
          p={3}
          borderRadius="md"
        >
          {errorList.map((error, idx) => (
            <Text key={idx} fontSize="sm" color="red.500">
              {error}
            </Text>
          ))}
        </Box>
      )}
    </>
  )
}

export default ExcelDragAndDrop
